import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Form, Button, Card, Row, Col, Alert, Spinner } from "react-bootstrap";
import { gymMembersAPI } from "../../api/gymMembers";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import {
  FaUser,
  FaPhone,
  FaEnvelope,
  FaHome,
  FaArrowLeft,
  FaSave,
  FaFileMedical,
} from "react-icons/fa";

const EditMember = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [member, setMember] = useState(null);

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    mobile_number: "",
    emergency_number: "",
    email: "",
    address: "",
    medical_history: "",
    dob: null,
  });

  useEffect(() => {
    fetchMemberDetails();
  }, [id]);

  const fetchMemberDetails = async () => {
    try {
      const response = await gymMembersAPI.getMemberById(id);
      setMember(response.data);

      // Format dates for date picker
      const dob = response.data.dob ? new Date(response.data.dob) : null;

      setFormData({
        first_name: response.data.first_name || "",
        last_name: response.data.last_name || "",
        mobile_number: response.data.mobile_number || "",
        emergency_number: response.data.emergency_number || "",
        email: response.data.email || "",
        address: response.data.address || "",
        medical_history: response.data.medical_history || "",
        dob: dob,
      });
    } catch (error) {
      console.error("Error fetching member details:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load member details",
      });
      navigate("/members");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleDateChange = (date) => {
    setFormData({
      ...formData,
      dob: date,
    });
  };

  const validateForm = () => {
    const errors = [];

    if (!formData.first_name.trim()) errors.push("First name is required");
    if (!formData.last_name.trim()) errors.push("Last name is required");

    if (!formData.mobile_number.trim()) {
      errors.push("Mobile number is required");
    } else if (!/^\d{10}$/.test(formData.mobile_number)) {
      errors.push("Mobile number must be 10 digits");
    }

    if (!formData.email.trim()) errors.push("Email is required");
    if (!/^\S+@\S+\.\S+$/.test(formData.email))
      errors.push("Invalid email format");

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validateForm();
    if (errors.length > 0) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        html: errors.map((err) => `<p>• ${err}</p>`).join(""),
      });
      return;
    }

    setSaving(true);
    setError("");

    try {
      // Format dates for API (YYYY-MM-DD)
      const formattedData = {
        ...formData,
        dob: formData.dob ? formData.dob.toISOString().split("T")[0] : null,
      };

      // Remove null/undefined fields
      Object.keys(formattedData).forEach((key) => {
        if (
          formattedData[key] === null ||
          formattedData[key] === undefined ||
          formattedData[key] === ""
        ) {
          delete formattedData[key];
        }
      });

      await gymMembersAPI.updateMember(id, formattedData);

      Swal.fire({
        icon: "success",
        title: "Updated!",
        text: "Member details updated successfully.",
        timer: 1500,
        showConfirmButton: false,
      });

      // Redirect back to members list
      setTimeout(() => {
        navigate("/members");
      }, 1500);
    } catch (error) {
      console.error("Error updating member:", error);
      setError(error.response?.data?.message || "Failed to update member");
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to update member",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading member details...</p>
      </div>
    );
  }

  if (!member) {
    return (
      <Alert variant="danger">
        Member not found.{" "}
        <Button variant="link" onClick={() => navigate("/members")}>
          Go back
        </Button>
      </Alert>
    );
  }

  return (
    <div className="edit-member-page">
      <div className="page-header mb-4">
        <Button
          variant="outline-secondary"
          onClick={() => navigate("/members")}
          className="me-3"
        >
          <FaArrowLeft /> Back to Members
        </Button>
        <h2 className="mb-0 d-flex align-items-center">
          <FaUser className="me-2" /> Edit Member: {member.first_name}{" "}
          {member.last_name}
        </h2>
      </div>

      {error && (
        <Alert variant="danger" onClose={() => setError("")} dismissible>
          {error}
        </Alert>
      )}

      <Card>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Card className="mb-4">
                  <Card.Header className="bg-primary text-white">
                    Personal Information
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>First Name *</Form.Label>
                          <div className="input-group">
                            <span className="input-group-text">
                              <FaUser />
                            </span>
                            <Form.Control
                              type="text"
                              name="first_name"
                              value={formData.first_name}
                              onChange={handleChange}
                              required
                            />
                          </div>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Last Name *</Form.Label>
                          <div className="input-group">
                            <span className="input-group-text">
                              <FaUser />
                            </span>
                            <Form.Control
                              type="text"
                              name="last_name"
                              value={formData.last_name}
                              onChange={handleChange}
                              required
                            />
                          </div>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-3">
                      <Form.Label>Date of Birth</Form.Label>
                      <DatePicker
                        selected={formData.dob}
                        onChange={handleDateChange}
                        dateFormat="yyyy-MM-dd"
                        className="form-control"
                        maxDate={new Date()}
                        showYearDropdown
                        dropdownMode="select"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Mobile Number *</Form.Label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <FaPhone />
                        </span>
                        <Form.Control
                          type="tel"
                          name="mobile_number"
                          value={formData.mobile_number}
                          onChange={handleChange}
                          pattern="[0-9]{10}"
                          maxLength="10"
                          required
                        />
                      </div>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Emergency Contact Number</Form.Label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <FaPhone />
                        </span>
                        <Form.Control
                          type="tel"
                          name="emergency_number"
                          value={formData.emergency_number}
                          onChange={handleChange}
                          pattern="[0-9]{10}"
                          maxLength="10"
                        />
                      </div>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Email *</Form.Label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <FaEnvelope />
                        </span>
                        <Form.Control
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </Form.Group>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={6}>
                <Card className="mb-4">
                  <Card.Header className="bg-info text-white">
                    <FaHome className="me-2" /> Address & Medical
                  </Card.Header>
                  <Card.Body>
                    <Form.Group className="mb-3">
                      <Form.Label>Address</Form.Label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <FaHome />
                        </span>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          name="address"
                          value={formData.address}
                          onChange={handleChange}
                        />
                      </div>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>
                        <FaFileMedical className="me-2" /> Medical History
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={8}
                        name="medical_history"
                        value={formData.medical_history}
                        onChange={handleChange}
                        placeholder="Any medical conditions, allergies, or special requirements..."
                      />
                    </Form.Group>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Member Info Summary */}
            <Card className="mb-4">
              <Card.Header className="bg-secondary text-white">
                Member Information Summary
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={3}>
                    <div className="mb-2">
                      <small className="text-muted">Member ID</small>
                      <div className="fw-bold">{member.member_id}</div>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="mb-2">
                      <small className="text-muted">Join Date</small>
                      <div className="fw-bold">
                        {member.created_at
                          ? new Date(member.created_at).toLocaleDateString()
                          : "N/A"}
                      </div>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="mb-2">
                      <small className="text-muted">Current Status</small>
                      <div className="fw-bold">
                        <span
                          className={`badge bg-${
                            member.is_active ? "success" : "secondary"
                          }`}
                        >
                          {member.is_active ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="mb-2">
                      <small className="text-muted">Last Updated</small>
                      <div className="fw-bold">
                        {member.updated_at
                          ? new Date(member.updated_at).toLocaleDateString()
                          : "N/A"}
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <div className="text-center">
              <Button
                variant="primary"
                type="submit"
                disabled={saving}
                className="me-3 px-4"
              >
                <FaSave className="me-2" />
                {saving ? "Saving Changes..." : "Save Changes"}
              </Button>

              <Button
                variant="outline-secondary"
                type="button"
                onClick={() => navigate("/members")}
              >
                Cancel
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default EditMember;
